/**
 * Property Form - Location Section with Map Coordinates and Geocoding
 */
import { useState } from "react";
import { MapPin, Search, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import apiClient from "@/utils/api";

const DEPARTMENTS = [
  "Guatemala", "Sacatepéquez", "Chimaltenango", "Escuintla", "Santa Rosa",
  "Sololá", "Quetzaltenango", "Retalhuleu", "San Marcos", "Huehuetenango",
  "Quiché", "Baja Verapaz", "Alta Verapaz", "Petén", "Izabal", 
  "Zacapa", "Chiquimula", "Jalapa", "Jutiapa", "El Progreso", "Totonicapán", "Suchitepéquez"
];

const PropertyLocation = ({ department, setDepartment, zone, setZone, address, setAddress, latitude, setLatitude, longitude, setLongitude }) => {
  const [geocoding, setGeocoding] = useState(false);

  const handleGeocode = async () => {
    const fullAddress = `${address}, ${zone}, ${department}`.trim();
    if (!fullAddress || fullAddress === ", , ") {
      toast.error("Por favor ingresa una dirección primero");
      return;
    }

    setGeocoding(true);
    try {
      const response = await apiClient.get('/geocode', {
        params: { address: fullAddress }
      });
      
      if (response.data.success) {
        setLatitude(response.data.latitude.toString());
        setLongitude(response.data.longitude.toString());
        toast.success("Coordenadas encontradas automáticamente");
      } else {
        toast.error(response.data.message || "No se encontraron coordenadas");
      }
    } catch (error) {
      toast.error("Error al buscar coordenadas");
    } finally {
      setGeocoding(false);
    }
  };

  return (
    <section className="bg-[#121212] border border-white/5 p-6 space-y-6">
      <h2 className="text-lg text-white font-medium">Ubicación</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm text-[#A3A3A3] mb-2">Departamento *</label>
          <Select value={department} onValueChange={setDepartment}>
            <SelectTrigger className="h-12 bg-black/40 border-white/10 text-white">
              <SelectValue placeholder="Seleccionar" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10 max-h-60">
              {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-sm text-[#A3A3A3] mb-2">Zona</label>
          <Input 
            value={zone} 
            onChange={(e) => setZone(e.target.value)} 
            placeholder="Zona 14" 
            className="h-12 bg-black/40 border-white/10 text-white" 
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm text-[#A3A3A3] mb-2">Dirección</label>
          <Input 
            value={address} 
            onChange={(e) => setAddress(e.target.value)} 
            placeholder="Dirección completa" 
            className="h-12 bg-black/40 border-white/10 text-white" 
          />
        </div>
      </div>
      
      {/* Map Coordinates */}
      <div className="border-t border-white/5 pt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MapPin className="text-[#D4AF37]" size={20} />
            <h3 className="text-white font-medium">Coordenadas del Mapa</h3>
            <span className="text-[#A3A3A3] text-xs">(opcional)</span>
          </div>
          <Button
            type="button"
            onClick={handleGeocode}
            disabled={geocoding}
            className="bg-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4AF37]/30 gap-2"
            size="sm"
          >
            {geocoding ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Search size={14} />
            )}
            Buscar automáticamente
          </Button>
        </div>
        <p className="text-[#A3A3A3] text-sm mb-4">
          Completa la dirección arriba y haz clic en "Buscar automáticamente", o ingresa las coordenadas manualmente.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-[#A3A3A3] mb-2">Latitud</label>
            <Input 
              type="number" 
              step="any"
              value={latitude} 
              onChange={(e) => setLatitude(e.target.value)} 
              placeholder="14.6349" 
              className="h-12 bg-black/40 border-white/10 text-white"
              data-testid="latitude-input"
            />
          </div>
          <div>
            <label className="block text-sm text-[#A3A3A3] mb-2">Longitud</label>
            <Input 
              type="number" 
              step="any"
              value={longitude} 
              onChange={(e) => setLongitude(e.target.value)} 
              placeholder="-90.5069" 
              className="h-12 bg-black/40 border-white/10 text-white"
              data-testid="longitude-input"
            />
          </div>
        </div>
        {latitude && longitude && (
          <p className="text-green-500 text-sm mt-2 flex items-center gap-1">
            <MapPin size={14} /> Coordenadas configuradas - Se mostrará en el mapa
          </p>
        )}
      </div>
    </section>
  );
};

export default PropertyLocation;
